import { trackAnalyticsException } from "utils/analytics";
import React from 'react';
import ReactDOM from 'react-dom';
import App from 'App.tsx';
// import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { version as dashboardVersion } from '../package.json';

// A stale-deploy SyntaxError: the browser fetched a hashed CRA chunk that no longer
// exists on the CDN, got index.html instead, and failed to parse it as JS. That
// always surfaces as "Unexpected token '<'" at 1:1 of the chunk file itself; a
// genuinely broken bundle fails elsewhere and still reports.
const isStaleChunkParseError = (exception) => {
  if (exception?.type !== 'SyntaxError') return false;
  if (!(exception.value || '').includes("Unexpected token '<'")) return false;
  const frames = exception.stacktrace?.frames;
  if (!frames?.length) return false;
  const topFrame = frames[frames.length - 1];
  if (topFrame?.lineno !== 1 || topFrame?.colno !== 1) return false;
  return /\/static\/js\/[^/]+\.chunk\.js$/.test(topFrame.filename || '');
};

// Every CRA build runs with NODE_ENV=production, so the deploy target comes from the host.
const SENTRY_ENVIRONMENTS = {
  'app.gastronaut.ai': 'production',
  'gastronaut-dashboard.netlify.app': 'production',
  'next.gastronaut.ai': 'next',
  'gastronaut-next.netlify.app': 'next',
  'staging.app.gastronaut.ai': 'staging',
  'gastronaut-staging-dashboard.netlify.app': 'staging',
};
const sentryEnvironment = SENTRY_ENVIRONMENTS[window.location.host] || 'preview';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || "",
  // CRA sets NODE_ENV=development for `npm start`, production for builds —
  // this disables reporting from the dev server. A production build still
  // reports wherever it runs, including when served locally.
  enabled: process.env.NODE_ENV === 'production',
  environment: sentryEnvironment,
  // Suffixed so errors from next, staging and previews never count toward a production release.
  release: sentryEnvironment === 'production' ? dashboardVersion : `${dashboardVersion}+${sentryEnvironment}`,
  integrations: [new BrowserTracing()],
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
  ignoreErrors: [
    // Browser-initiated fetch/media aborts (DOMException 20), no stack, unactionable.
    'AbortError: The operation was aborted',
    // Firestore offline error from mobile network flakiness; SDK retries itself.
    'Failed to get document because the client is offline',
    // Autoplay-policy rejections of the alarm/notification sound. Guarded at the
    // call site in utils/playAudio.ts; these keep cached bundles quiet too.
    // Chrome appends a help URL to the first one, so both match as substrings.
    "play() failed because the user didn't interact with the document first",
    'The play() request was interrupted',
  ],
  beforeSend(event, hint) {
    trackAnalyticsException(hint?.originalException);
    const errorMsg = event.exception?.values?.[0]?.value || event.message || '';
    if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('Network Error')) return null;
    if (isStaleChunkParseError(event.exception?.values?.[0])) return null;
    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.data) {
      if (breadcrumb.data.password) breadcrumb.data.password = '[REDACTED]';
      if (breadcrumb.data.token) breadcrumb.data.token = '[REDACTED]';
      if (breadcrumb.data.Authorization) breadcrumb.data.Authorization = '[REDACTED]';
    }
    return breadcrumb;
  }
});

if (process.env.NODE_ENV === 'production') {
  // Only suppress console.log (noise). console.error/warn must stay alive — Sentry
  // wraps them during init to collect breadcrumbs; overwriting them breaks that.
  console.log = () => {};
}

ReactDOM.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred.</p>}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorkerRegistration.unregister();
